/**
 * Minimal polyfill for the ES2025 Iterator Helpers `toArray()` method.
 *
 * `@mui/x-charts-pro`'s SankeyChart calls `nodeMap.values().toArray()`
 * (`SankeyChart/seriesConfig/getSeriesWithDefaultValues.js`) unconditionally.
 * `Iterator.prototype.toArray` only landed in very recent engines (Chrome 122+,
 * Node 22+), so on older browsers — and notably the Electron build Cypress runs —
 * the call throws `toArray is not a function` and crashes the chart.
 *
 * We patch the shared `%IteratorPrototype%` (the prototype every built-in
 * iterator, including Map/Set/Array iterators, inherits from) so the method is
 * available everywhere. Guarded so it no-ops on engines that already ship it.
 *
 * Imported first in `src/index.js` so it runs before any chart renders.
 */

type WithToArray = { toArray?: () => unknown[] } | null | undefined;

/**
 * Adds a spec-compatible `toArray()` to the given iterator prototype if it is
 * missing. Exported for testing; the module-level call below installs it on the
 * real `%IteratorPrototype%`.
 */
export const polyfillIteratorToArray = (proto: WithToArray): void => {
  if (proto && typeof proto.toArray !== 'function') {
    Object.defineProperty(proto, 'toArray', {
      configurable: true,
      writable: true,
      value: function toArray(this: Iterable<unknown>) {
        return Array.from(this);
      },
    });
  }
};

// An Array iterator inherits from %IteratorPrototype%; walk up one level to it.
polyfillIteratorToArray(
  Object.getPrototypeOf(Object.getPrototypeOf([][Symbol.iterator]())),
);
